// Centralized invoice status configuration
// This file contains all invoice status-related mappings and configurations

export interface InvoiceStatusConfig {
  code: string;           // Status code (e.g., 'pending', 'approved', 'declined')
  displayName: string;    // Human-readable display name (e.g., 'Pending', 'Approved')
  description: string;    // Detailed description of the status
  variant: 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'default'; // UI variant for StatusBadge
  order: number;          // Display order for sorting/filtering
}

// Available invoice statuses
export const AVAILABLE_INVOICE_STATUSES: InvoiceStatusConfig[] = [
  {
    code: 'submitted',
    displayName: 'Submitted',
    description: 'The invoice has been uploaded and is pending review.',
    variant: 'warning', // Jaffa color
    order: 1
  },
  {
    code: 'approved',
    displayName: 'Approved',
    description: 'The invoice has been approved.',
    variant: 'success', // Shamrock green
    order: 2
  },
  {
    code: 'refunded',
    displayName: 'Refunded',
    description: 'The invoice has been approved and refunded.',
    variant: 'success', // Shamrock green
    order: 2
  },
  {
    code: 'declined',
    displayName: 'Declined',
    description: 'The invoice has been declined.',
    variant: 'error', // Wild watermelon
    order: 3
  },
  {
    code: 'rejected',
    displayName: 'Rejected',
    description: 'The invoice has been rejected.',
    variant: 'error', // Wild watermelon
    order: 3
  }
];

// Create lookup maps for performance
export const INVOICE_STATUS_BY_CODE = AVAILABLE_INVOICE_STATUSES.reduce((acc, status) => {
  acc[status.code] = status;
  return acc;
}, {} as Record<string, InvoiceStatusConfig>);

export const INVOICE_STATUS_DISPLAY_NAMES = AVAILABLE_INVOICE_STATUSES.reduce((acc, status) => {
  acc[status.code] = status.displayName;
  return acc;
}, {} as Record<string, string>);

export const INVOICE_STATUS_DESCRIPTIONS = AVAILABLE_INVOICE_STATUSES.reduce((acc, status) => {
  acc[status.code] = status.description;
  return acc;
}, {} as Record<string, string>);

export const INVOICE_STATUS_VARIANTS = AVAILABLE_INVOICE_STATUSES.reduce((acc, status) => {
  acc[status.code] = status.variant;
  return acc;
}, {} as Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'default'>);

// Helper functions
export const getInvoiceStatusByCode = (code: string): InvoiceStatusConfig | undefined => {
  return INVOICE_STATUS_BY_CODE[code];
};

export const getInvoiceStatusDisplayName = (code: string): string => {
  return INVOICE_STATUS_DISPLAY_NAMES[code] || code.charAt(0).toUpperCase() + code.slice(1);
};

// Get localized display name (requires translation function)
export const getInvoiceStatusLocalizedDisplayName = (code: string, t: (key: string) => string): string => {
  const statusKey = `invoice_status_${code}`;
  return t(statusKey) || INVOICE_STATUS_DISPLAY_NAMES[code] || code.charAt(0).toUpperCase() + code.slice(1);
};

export const getInvoiceStatusDescription = (code: string): string => {
  return INVOICE_STATUS_DESCRIPTIONS[code] || 'Unknown status';
};

export const getInvoiceStatusVariant = (code: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'default' => {
  return INVOICE_STATUS_VARIANTS[code] || 'default';
};

// Get all status codes
export const getAllInvoiceStatusCodes = (): string[] => {
  return AVAILABLE_INVOICE_STATUSES.map(status => status.code);
};

// Get all status display names
export const getAllInvoiceStatusDisplayNames = (): string[] => {
  return AVAILABLE_INVOICE_STATUSES.map(status => status.displayName);
};

// Get statuses ordered by display order
export const getInvoiceStatusesOrdered = (): InvoiceStatusConfig[] => {
  return [...AVAILABLE_INVOICE_STATUSES].sort((a, b) => a.order - b.order);
};

// Get status codes ordered by display order
export const getInvoiceStatusCodesOrdered = (): string[] => {
  return getInvoiceStatusesOrdered().map(status => status.code);
};

// Check if a status code is supported
export const isInvoiceStatusSupported = (code: string): boolean => {
  return code in INVOICE_STATUS_BY_CODE;
};

// Get default status
export const getDefaultInvoiceStatus = (): string => {
  return 'submitted';
};

// Get statuses by variant
export const getInvoiceStatusesByVariant = (variant: 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'default'): string[] => {
  return AVAILABLE_INVOICE_STATUSES
    .filter(status => status.variant === variant)
    .map(status => status.code);
};

// Legacy mappings for backward compatibility
export const INVOICE_STATUS_OPTIONS = AVAILABLE_INVOICE_STATUSES.map(status => ({
  value: status.code,
  label: status.displayName
}));

// Common status groups for filtering
export const INVOICE_STATUS_GROUPS = {
  active: ['submitted'],
  completed: ['refunded'],
  rejected: ['declined']
};
